import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { UsageBadge } from "../components/usage-badge";
import { Button } from "../components/ui/button";
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
} from "../components/ui/card";
import { Skeleton } from "../components/ui/skeleton";
import { Plus, Settings, Zap } from "lucide-react";

export function SettingsPage() {
  const account = useQuery({
    queryKey: ["account"],
    queryFn: api.getAccount,
  });

  const funnels = useQuery({
    queryKey: ["funnels"],
    queryFn: api.listFunnels,
  });

  const wazzup = useQuery({
    queryKey: ["wazzup-status"],
    queryFn: api.getWazzupStatus,
  });

  if (account.isLoading || funnels.isLoading) {
    return (
      <div className="space-y-4 p-4">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-6 w-64" />
        <div className="space-y-3">
          <Skeleton className="h-24 w-full" />
          <Skeleton className="h-24 w-full" />
        </div>
      </div>
    );
  }

  if (account.error || funnels.error) {
    return (
      <div className="p-4 text-destructive">
        Ошибка загрузки: {(account.error ?? funnels.error)?.message}
      </div>
    );
  }

  const accountData = account.data!;
  const funnelList = funnels.data!;

  return (
    <div className="space-y-6 p-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-lg font-semibold">AI CRM Connector</h1>
          <div className="mt-1">
            <UsageBadge account={accountData} />
          </div>
        </div>
      </div>

      {wazzup.data && !wazzup.data.connected && (
        <Card className="border-amber-300 bg-amber-50">
          <CardContent className="flex items-center justify-between gap-4 p-4">
            <p className="text-sm">
              Подключите Wazzup, чтобы агенты могли отвечать в WhatsApp и Telegram.
            </p>
            <Link to="/settings/wazzup">
              <Button variant="outline" size="sm">
                Подключить
              </Button>
            </Link>
          </CardContent>
        </Card>
      )}

      <div className="flex items-center justify-between">
        <h2 className="text-base font-medium">Воронки</h2>
        <Link to="/funnels/new">
          <Button size="sm">
            <Plus className="mr-1 h-4 w-4" />
            Создать
          </Button>
        </Link>
      </div>

      {funnelList.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <Zap className="mb-4 h-10 w-10 text-muted-foreground" />
            <p className="mb-2 text-sm font-medium">
              У вас пока нет воронок
            </p>
            <p className="mb-4 text-sm text-muted-foreground">
              Создайте первую воронку, чтобы подключить AI-агента
            </p>
            <Link to="/funnels/new">
              <Button size="sm">Создать первую воронку</Button>
            </Link>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {funnelList.map((funnel) => {
            const hasChannels = funnel.channels.length > 0;
            const isLive = funnel.active && hasChannels;
            const statusLabel = isLive
              ? "активен"
              : !hasChannels
                ? "нет каналов"
                : "на паузе";
            const channelSummary = hasChannels
              ? Array.from(new Set(funnel.channels.map((c) => c.type))).join(", ")
              : "каналы не подключены";

            return (
              <Link key={funnel.id} to={`/funnels/${funnel.id}`}>
                <Card className="transition-colors hover:bg-accent/50">
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-sm">{funnel.name}</CardTitle>
                      <div className="flex items-center gap-2">
                        <span className="flex items-center gap-1.5 text-xs text-muted-foreground">
                          <span
                            className={
                              "inline-block h-2 w-2 rounded-full " +
                              (isLive
                                ? "bg-green-500"
                                : !hasChannels
                                  ? "bg-amber-500"
                                  : "bg-gray-400")
                            }
                          />
                          {statusLabel}
                        </span>
                        <Settings className="h-4 w-4 text-muted-foreground" />
                      </div>
                    </div>
                    <CardDescription className="text-xs">
                      {channelSummary}
                    </CardDescription>
                  </CardHeader>
                </Card>
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
}
